import React from 'react';
import { Avatar, Button } from '../../../../components';
import { FormattedMessage } from 'react-intl';
import css from './CartCard.module.css';
import CartListing from '../CartListing/CartListing';
import classNames from 'classnames';
import NamedLink from '../../../../components/NamedLink/NamedLink';
import { createSlug } from '../../../../util/urlHelpers';

/**
 * @param {Object} props
 * @param {string} props.providerId
 * @param {Object[]} props.items
 * @param {boolean} props.inProgress
 * @param {string} props.updateCartWithSizeQuantitiesError
 * @param {function} props.onUpdateCartWithSizeQuantities
 * @param {function} props.onRemoveFromCart
 * @param {Object} props.firstAuthor
 * @param {string} props.providerTotalFormatted
 * @param {number} props.providerTotalItems
 * @param {function} props.onCheckout
 * @param {string} [props.className]
 * @returns {JSX.Element}
 * @description CartCard component that displays all items from a specific provider
 * @example
 * <CartCard
 *   providerId="123"
 *   items={[{ listing: {...}, size: 'S', quantity: 1 }, { listing: {...}, size: 'M', quantity: 2 }]}
 *   inProgress={false}
 *   updateCartWithSizeQuantitiesError="Error"
 *   onUpdateCartWithSizeQuantities={() => {}}
 *   onRemoveFromCart={() => {}}
 *   firstAuthor={{ id: '123', attributes: { profile: { displayName: 'John Doe' } } }}
 *   providerTotalFormatted="100"
 *   providerTotalItems={3}
 * />
 */
const CartCard = ({
  providerId,
  items,
  inProgress,
  updateCartWithSizeQuantitiesError,
  onUpdateCartWithSizeQuantities,
  onRemoveFromCart,
  firstAuthor,
  providerTotalFormatted,
  providerTotalItems,
  className,
  onCheckout,
}) => {
  const classes = classNames(css.root, className);

  if (!items || items.length === 0) {
    return null;
  }

  // Check if any item has insufficient stock
  const disabledCheckout = items.some(({ listing, quantity, size }) => {
    const actualStock = listing?.attributes?.publicData?.sizesWithQuantity?.[size];
    return quantity > actualStock || !actualStock;
  });

  // Group items by listing for better organization
  const itemsByListing = items.reduce((acc, item) => {
    const { listingId, listing } = item;
    if (!acc[listingId]) {
      acc[listingId] = {
        listing,
        items: [],
      };
    }
    acc[listingId].items.push(item);
    return acc;
  }, {});

  return (
    <div key={providerId} className={classes}>
      <div className={css.cartListings}>
        {Object.entries(itemsByListing).map(([listingId, listingData]) => {
          const { listing, items: listingItems } = listingData;
          return (
            <div key={listingId} className={css.listingGroup}>
              {listingItems.map(({ size, sizeLabel, quantity }) => (
                <CartListing
                  key={`${listingId}-${size}`}
                  className={css.cartCard}
                  listing={listing}
                  size={size}
                  sizeLabel={sizeLabel}
                  quantity={quantity}
                  removeError={updateCartWithSizeQuantitiesError}
                  removeFromCart={() => onRemoveFromCart(providerId, listingId, size)}
                  updateQuantity={newQuantity =>
                    newQuantity > 0
                      ? onUpdateCartWithSizeQuantities(providerId, listingId, size, newQuantity)
                      : onRemoveFromCart(providerId, listingId, size)
                  }
                />
              ))}
            </div>
          );
        })}
      </div>

      <div className={css.checkoutButton}>
        <div>
          <FormattedMessage id="CartPage.cartWithProvider" />
          <NamedLink
            name="ProfilePage"
            params={{ id: firstAuthor?.id?.uuid }}
            className={css.providerLink}
          >
            {firstAuthor?.attributes?.profile?.displayName}
          </NamedLink>
        </div>
        <div className={css.subtotalContainer}>
          <span className={css.subtotal}>
            <FormattedMessage id="CartPage.subtotal" />
            <span className={css.subtotalPrice}>{providerTotalFormatted}</span>
          </span>
          <span className={css.totalItems}>
            <FormattedMessage id="CartPage.totalItems" values={{ quantity: providerTotalItems }} />
          </span>
        </div>
        <Button
          className={css.checkoutButtonCta}
          disabled={inProgress || disabledCheckout}
          inProgress={inProgress}
          onClick={onCheckout}
        >
          <FormattedMessage id="CartCard.checkout" />
        </Button>
      </div>
    </div>
  );
};

export default CartCard;
